import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useToasts } from 'react-toast-notifications';
import {
  ContactType,
  ContactTypeSelector,
  ContactInput,
  Icons,
  colors,
} from '@united-talent-agency/components';
import {
  existingContactNotPhone,
  existingContactPhone,
  formatContactForDisplay,
} from '../../../support/contact';
import { fromStringToE164, isAcceptablePhoneNumber } from '@united-talent-agency/components';
import PhoneNumber from 'awesome-phonenumber';
import { AsYouType, getExampleNumber } from 'libphonenumber-js';
import examples from 'libphonenumber-js/mobile/examples';
import { getPrivateContacts } from '../../../api/people';
import {
  useUpdateContactMutation,
  useDeleteContactMutation,
  CallRecordsDocument,
  DeskDocument,
} from '../../../generated/graphql';
import { CALL_ROW, CONTACT_INPUT } from '../../../support/cypressTags';
import ConfirmationModal, { BoldText } from '../../ConfirmationModal';
import {
  ContactDisplayContainer,
  ContactInputContainer,
  TableCell,
  EditIconButton,
  ContactContentWrapper,
  ContactTypeWrapper,
  ContactTextSpan,
} from './styles';

/**
 * Inline editable contact
 * contact value with type icon is displayed, with single click
 * allowing an existing contact for a master-data-person, or a desk (personal) contact
 * to be selected for replacement OR if Add Contact is chosen
 * a new contact will be created and selected for replacement
 * @param {*} props (contact, onCancel, recipient, deskAddressBookEntryContacts = [], communicationRecord, onSave)
 * @returns value of the contact, or editing components
 */
const InlineEditableContact = ({
  callTodo = {},
  deskId,
  recipient,
  deskAddressBookEntryContacts = [],
  onSave,
}) => {
  // Feature flags
  const { editContactsEnabled } = useFlags();
  const { addToast } = useToasts();

  // Mutations with refetch
  const [updateContactMutation] = useUpdateContactMutation({
    refetchQueries: [CallRecordsDocument, DeskDocument],
  });
  const [deleteContactMutation] = useDeleteContactMutation({
    refetchQueries: [CallRecordsDocument, DeskDocument],
  });

  // States
  const [innerContact, setInnerContact] = useState(callTodo?.contact);
  const [newContactType, setNewContactType] = useState('');
  const [newContactValue, setNewContactValue] = useState('');
  const [createNewContact, setCreateNewContact] = useState(false);
  const [editContact, setEditContact] = useState(false);
  const [contactInputError, setContactInputError] = useState(null);
  const [privateContacts, setPrivateContacts] = useState([]);
  const newContactValueRef = useRef(newContactValue);
  // Edit/Delete states
  const [editingContact, setEditingContact] = useState(null);
  const [contactToDelete, setContactToDelete] = useState(null);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // this effect fetches the private contacts once edit-mode "editContact" is true
  useEffect(() => {
    if (editContact && recipient?._id) {
      getPrivateContacts(recipient._id).then((response) => {
        setPrivateContacts(response?.body?.privateContacts || []);
      });
    }
  }, [editContact, recipient]);

  useEffect(() => {
    newContactValueRef.current = newContactValue;
  }, [newContactValue]);

  // this sets the inner contact on load
  useEffect(() => {
    if (typeof callTodo?.contact !== 'undefined' && callTodo?.contact !== innerContact) {
      setInnerContact(callTodo?.contact);
    }
  }, [callTodo?.contact, innerContact]);

  const formattedContact = useMemo(() => formatContactForDisplay(innerContact), [innerContact]);

  //combine "master data" public contacts with "master data" private contacts (that their desk has access to)
  const masterDataContacts = useMemo(
    () => (recipient ? (recipient.contacts || []).concat(privateContacts) : []),
    [privateContacts, recipient]
  );

  //combine masterDataContacts with desk address book entries (just fall-through logic, should never be both?)
  const existingContacts = useMemo(
    () =>
      masterDataContacts
        .concat(deskAddressBookEntryContacts || [])
        .map(({ _id, contactType = 'Unknown', contact = '', primary }) => ({
          _id,
          contactType,
          value: contact,
          primary,
          private: contactType && !contact,
        })),
    [deskAddressBookEntryContacts, masterDataContacts]
  );

  const phoneErrorMessage = useMemo(() => {
    const asYouType = new AsYouType('ZZ');
    asYouType.input(newContactValue || '');

    const selectedCountry = PhoneNumber.getRegionCodeForCountryCode(asYouType.getCallingCode());

    let userRegion = 'US';
    try {
      userRegion = navigator.language.split('-')[1] || 'US';
    } catch {
      // could not get browser language
    }

    const format = selectedCountry === userRegion ? 'NATIONAL' : 'INTERNATIONAL';
    const phoneExamples = getExampleNumber(selectedCountry || userRegion, examples);

    let examplesString = '';
    if (phoneExamples) {
      examplesString = phoneExamples.format(format);
    }

    return 'Please enter a valid number, `' + examplesString + '`';
  }, [newContactValue]);

  // TODO: When removing editContactsEnabled feature flag, remove the flag check and keep only the Outlook message
  // Tooltip logic:
  // - Employee: always show Workday message
  // - Flag true + Personal/Desk: can edit, no tooltip shown (canEditContact=true)
  // - Flag true + Outlook: show Outlook-only message
  // - Flag false + any: show original message with both Outlook and Personal
  const tooltipMessage = useMemo(() => {
    if (callTodo.recipient?.type === 'Employee') {
      return 'Contact information for an employee can only be edited (by them) in Workday.';
    }

    if (editContactsEnabled) {
      return 'Phonesheet cannot edit Outlook (O) contacts.';
    }

    return 'Phonesheet cannot edit Outlook (O) or Personal (P) contacts.';
  }, [callTodo.recipient?.type, editContactsEnabled]);

  // Check if this is a Personal contact (not Outlook, not linked to a person)
  const isPersonalContact = useMemo(() => {
    return !recipient && callTodo?.recipientType !== 'Outlook';
  }, [recipient, callTodo?.recipientType]);

  // Can add contacts - original logic (no feature flag)
  // TODO: When removing editContactsEnabled feature flag, consolidate canAddContact and canEditContact into a single logic
  const canAddContact = useMemo(() => {
    // Personal contacts (no recipient, not Outlook) can add contacts when feature flag is enabled
    // This allows adding contacts even when the addressBook entry has no contacts yet
    if (editContactsEnabled && isPersonalContact) {
      return true;
    }

    return (
      recipient &&
      recipient?._id?.length > 0 &&
      recipient?.type !== 'Employee' &&
      (recipient?.canEdit ?? true)
    );
  }, [editContactsEnabled, isPersonalContact, recipient]);

  // Can edit/delete contacts - requires feature flag + allowed types
  // TODO: When removing editContactsEnabled feature flag, consolidate canAddContact and canEditContact into a single logic
  const canEditContact = useMemo(() => {
    if (!editContactsEnabled) {
      return false;
    }

    // Personal contacts (no recipient, not Outlook) can be edited
    // This allows editing even when the addressBook entry has no contacts yet
    if (isPersonalContact) {
      return true;
    }

    if (!canAddContact) {
      return false;
    }

    const recipientType = recipient?.type;
    return (
      recipientType === 'Client' ||
      recipientType === 'Industry Contact' ||
      recipientType?.includes('Desk') ||
      recipientType?.includes('Personal')
    );
  }, [editContactsEnabled, canAddContact, isPersonalContact, recipient]);

  // True when the person has exactly one deletable (master-data) contact left,
  // in which case deleting it should be disabled rather than leaving them with none.
  // Count distinct contacts shown in the UI list (public + accessible private).
  // Deduped by _id when present because recipient.contacts can already include
  // accessible private contacts that are also present in the separately-fetched
  // privateContacts; contacts without an _id fall back to contactType+value so
  // several of them don't collapse into a single "undefined" bucket.
  const isOnlyMasterDataContact = useMemo(
    () =>
      new Set(
        masterDataContacts.map((c) =>
          c._id ? `id:${c._id}` : `val:${c.contactType ?? ''}::${c.contact ?? ''}`
        )
      ).size === 1,
    [masterDataContacts]
  );

  const canDeleteContact = canEditContact && !isOnlyMasterDataContact;

  const handleEditContact = useCallback((contact) => {
    setEditingContact(contact);
    setNewContactType(contact.contactType);
    setNewContactValue(contact.value);
    setEditContact(false);
  }, []);

  const handleDeleteContact = useCallback((contact) => {
    setContactToDelete(contact);
    setShowDeleteConfirmation(true);
    setEditContact(false);
  }, []);

  const handleCancelEdit = useCallback(() => {
    setEditingContact(null);
    setNewContactType('');
    setNewContactValue('');
    setContactInputError(null);
  }, []);

  const handleSaveEdit = useCallback(
    async ({ contactType, contact }) => {
      if (!deskId || !editingContact?._id) {
        setContactInputError('Unable to update contact');
        return;
      }

      // Validate phone/fax numbers before saving
      const isPhoneOrFax = /Phone|Fax Number/.test(contactType);
      if (isPhoneOrFax && !isAcceptablePhoneNumber(contact || '')) {
        setContactInputError(phoneErrorMessage);
        return;
      }

      setIsSaving(true);
      try {
        const result = await updateContactMutation({
          variables: {
            deskId,
            personId: recipient?._id || null,
            contactId: editingContact._id,
            input: {
              contact,
              contactType,
            },
            callTodoId: callTodo?._id || null,
          },
        });

        if (result?.data?.updateContact?.success) {
          setEditingContact(null);
          setNewContactType('');
          setNewContactValue('');
          setContactInputError(null);
          addToast('Contact updated successfully.', { appearance: 'success', autoDismiss: true });
        } else {
          setContactInputError(result?.data?.updateContact?.message || 'Failed to update contact');
          addToast('Failed to update contact. Please try again.', {
            appearance: 'error',
            autoDismiss: true,
          });
        }
      } catch (error) {
        console.error('Error updating contact:', error);
        setContactInputError('Failed to update contact');
        addToast('Failed to update contact. Please try again.', {
          appearance: 'error',
          autoDismiss: true,
        });
      } finally {
        setIsSaving(false);
      }
    },
    [
      addToast,
      callTodo?._id,
      deskId,
      editingContact,
      phoneErrorMessage,
      recipient?._id,
      updateContactMutation,
    ]
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!deskId || !contactToDelete?._id) {
      return;
    }

    setIsDeleting(true);
    try {
      const result = await deleteContactMutation({
        variables: {
          deskId,
          personId: recipient?._id || null,
          contactId: contactToDelete._id,
          callTodoId: callTodo?._id || null,
        },
      });

      if (result?.data?.deleteContact?.success) {
        setContactToDelete(null);
        setShowDeleteConfirmation(false);
        addToast('Contact deleted successfully.', { appearance: 'success', autoDismiss: true });
        // refetchQueries is configured in deleteContactMutation, so the list will update automatically
      } else {
        console.error('Failed to delete contact:', result?.data?.deleteContact?.message);
        addToast('Failed to delete contact. Please try again.', {
          appearance: 'error',
          autoDismiss: true,
        });
      }
    } catch (error) {
      console.error('Error deleting contact:', error);
      addToast('Failed to delete contact. Please try again.', {
        appearance: 'error',
        autoDismiss: true,
      });
    } finally {
      setIsDeleting(false);
    }
  }, [deskId, contactToDelete, recipient, deleteContactMutation, addToast, callTodo?._id]);

  const handleCancelDelete = useCallback(() => {
    setContactToDelete(null);
    setShowDeleteConfirmation(false);
  }, []);

  const handleExistingContactSelected = useCallback(
    ({ contactType, value }) => {
      const isPhoneOrFax = /Phone|Fax Number/.test(contactType);

      // Only apply extension conversion for phone/fax contacts
      // For other contact types (email, message), just use the value as-is
      const cleanedValue = isPhoneOrFax
        ? value
            .replace(/\s*ext\.?\s*/gi, ';') // Handle 'ext.' and 'ext' with optional spaces
            .replace(/\s+x\s*/gi, ';') // Handle ' x ' or ' x' separator
            .replace(/[- )(]/g, '') // Remove spaces, hyphens, parentheses
        : value;

      const selectedContact = {
        contactType,
        contact: cleanedValue,
      };

      'contact' in callTodo || (callTodo.contact = {});
      const existing = /Phone|Fax Number/.test(selectedContact.contactType)
        ? existingContactPhone(recipient, selectedContact)
        : existingContactNotPhone(recipient, selectedContact);

      const contact = existing || selectedContact;
      const contactInfo = cleanedValue;

      onSave && onSave({ contact, contactInfo });
    },
    [callTodo, onSave, recipient]
  );

  const handleNewContactCreated = useCallback(() => {
    const isOfficeExtensionOnly =
      newContactValueRef.current &&
      newContactValueRef.current.includes(';') &&
      newContactValueRef.current.split(';')[0].length === 0 &&
      newContactValueRef.current.split(';')[1].length > 0;
    if (isOfficeExtensionOnly) {
      setContactInputError('Please enter a valid contact');
    } else if (onSave) {
      const newContact = {
        contact: newContactValueRef.current,
        contactType: newContactType,
      };

      onSave({
        ...callTodo,
        contact: newContact,
        contactInfo: newContactValueRef.current,
        isPersonalContact,
        deskId,
      });
      setNewContactValue('');
    }
  }, [callTodo, deskId, isPersonalContact, newContactType, onSave]);

  const handleOnChangeContactInput = useCallback(({ contactType, contact }) => {
    setNewContactType(contactType);
    setNewContactValue(contact);
    setContactInputError(null);
  }, []);

  const handleOnCancelContactInput = useCallback(() => {
    setCreateNewContact(false);
    setInnerContact(callTodo?.contact);
    setNewContactValue('');
    setContactInputError(null);
  }, [callTodo?.contact]);

  const handleOnBlurContactInput = useCallback(() => {
    if (!newContactValueRef.current) {
      return;
    }

    const isPhoneOrFax = /Phone|Fax Number/.test(newContactType);
    const invalidPhoneOrFax =
      newContactType && isPhoneOrFax && !isAcceptablePhoneNumber(newContactValueRef.current || '');
    if (invalidPhoneOrFax || (!isPhoneOrFax && !newContactValueRef.current)) {
      setContactInputError(isPhoneOrFax ? phoneErrorMessage : 'Please enter a valid contact');
    } else {
      handleNewContactCreated();
      setCreateNewContact(false);
      setContactInputError(null);
    }
  }, [handleNewContactCreated, newContactType, phoneErrorMessage]);

  const isPhoneNumber = useMemo(() => {
    if (!/Phone/i.test(formattedContact.contactType)) {
      return false;
    }

    const contact = innerContact?.contact || '';

    // First try to validate the original number
    if (isAcceptablePhoneNumber(contact)) {
      return true;
    }

    // Fallback: try adding +1 prefix
    return isAcceptablePhoneNumber(`+1${contact}`);
  }, [formattedContact, innerContact?.contact]);

  const handleContactClick = useCallback(() => {
    if (isPhoneNumber) {
      const e164Result = fromStringToE164(innerContact?.contact);
      let cleanedNumber;

      if (
        e164Result &&
        typeof e164Result === 'object' &&
        e164Result.countryCode &&
        e164Result.number
      ) {
        cleanedNumber = `+${e164Result.countryCode}${e164Result.number}`;
      } else if (typeof e164Result === 'string') {
        cleanedNumber = e164Result;
      } else {
        cleanedNumber = formattedContact.contact.replace(/[^+\d]/g, '');
      }

      window.open(`zoomphonecall:${encodeURIComponent(cleanedNumber)}`, '_self');
    }
  }, [isPhoneNumber, formattedContact.contact, innerContact?.contact]);

  const handleEditClick = useCallback((e) => {
    e.stopPropagation();
    setEditContact(true);
  }, []);

  return (
    <TableCell>
      {!createNewContact && !editingContact && (
        <>
          <ContactDisplayContainer id="contact-container" editContact={editContact}>
            <ContactContentWrapper onClick={handleContactClick} isClickable={isPhoneNumber}>
              <ContactTypeWrapper>
                <ContactType type={formattedContact.contactType} inline />
              </ContactTypeWrapper>
              <ContactTextSpan
                data-cy={CALL_ROW.CONTACT}
                text={formattedContact.contact}
                isClickable={isPhoneNumber}
              />
            </ContactContentWrapper>
            <EditIconButton title="View contacts" onClick={handleEditClick}>
              <Icons.ChevronDown width={10} height={10} />
            </EditIconButton>
          </ContactDisplayContainer>
          {!!editContact && (
            <ContactTypeSelector
              dropdownOnly
              dropdownStyle={{
                position: 'absolute',
              }}
              existing={existingContacts}
              isOpen={editContact}
              onClose={() => setEditContact(false)}
              addable={canAddContact}
              canEdit={canEditContact}
              canDelete={canDeleteContact}
              onEdit={handleEditContact}
              onDelete={handleDeleteContact}
              tooltipMessage={tooltipMessage}
              deleteTooltipMessage={
                isOnlyMasterDataContact ? "You can't delete the only contact" : undefined
              }
              value={{
                contactType: innerContact?.contactType || 'Unknown',
                value: innerContact?.contact,
              }}
              cypressTags={{
                existingContact: CONTACT_INPUT.EXISTING_CONTACT,
                addContact: CONTACT_INPUT.ADD_CONTACT,
                contactType: CONTACT_INPUT.CONTACT_TYPE,
              }}
              onChange={(selected) => {
                if (!selected.value) {
                  setNewContactType(selected.contactType);
                  setCreateNewContact(true);
                } else {
                  handleExistingContactSelected(selected);
                }
                setEditContact(false);
              }}
            />
          )}
          <ConfirmationModal
            isOpen={showDeleteConfirmation && !!contactToDelete}
            onCancel={handleCancelDelete}
            onConfirm={handleConfirmDelete}
            title="Delete Confirmation"
            loading={isDeleting}
          >
            Delete <BoldText>{contactToDelete?.contactType}</BoldText>: {contactToDelete?.value}?
          </ConfirmationModal>
        </>
      )}
      {!!editingContact && (
        <ContactInputContainer>
          <ContactInput
            focused
            editMode
            loading={isSaving}
            contact={{ contact: newContactValue, contactType: newContactType }}
            onChange={handleOnChangeContactInput}
            onCancel={handleCancelEdit}
            onSave={handleSaveEdit}
            onBlur={() => {}}
            existingContacts={existingContacts}
            error={contactInputError}
            outline={false}
            containerStyle={{
              border: `1px solid ${colors.nobel}`,
              filter: `drop-shadow(0 2px 10px rgba(173, 165, 159, 0.3))`,
            }}
            cypressTags={{
              existingContact: CONTACT_INPUT.EXISTING_CONTACT,
              addContact: CONTACT_INPUT.ADD_CONTACT,
              contactType: CONTACT_INPUT.CONTACT_TYPE,
              phoneNumberInput: CONTACT_INPUT.PHONE_NUMBER_INPUT,
            }}
          />
        </ContactInputContainer>
      )}
      {!!createNewContact && (
        <ContactInputContainer>
          <ContactInput
            focused
            editMode
            contact={{ contact: newContactValue, contactType: newContactType }}
            onChange={handleOnChangeContactInput}
            onCancel={handleOnCancelContactInput}
            onSave={handleOnBlurContactInput}
            onBlur={() => {}}
            existingContacts={existingContacts}
            error={contactInputError}
            cypressTags={{
              existingContact: CONTACT_INPUT.EXISTING_CONTACT,
              addContact: CONTACT_INPUT.ADD_CONTACT,
              contactType: CONTACT_INPUT.CONTACT_TYPE,
              phoneNumberInput: CONTACT_INPUT.PHONE_NUMBER_INPUT,
            }}
          />
        </ContactInputContainer>
      )}
    </TableCell>
  );
};

export default InlineEditableContact;
